// GA4 events for the customer journey, on top of what the Google & YouTube app already sends (page_view,
// view_item, view_item_list on collection pages, add_to_cart, view_cart, remove_from_cart, checkout, purchase):
// - customer_type user property (business = prices excl. VAT, consumer = incl.) and vat_switch
// - view_item_list for product lists outside the collection grid, select_item for clicks in every list
// - search_results (with the result count), filter_apply and sort_change, view_sold_out_item
// The app puts gtag on the page once the visitor allows analytics cookies, often after this script has run, so
// events wait in a queue for up to 30 seconds and are dropped without consent.
(() => {
  if (window.agAnalytics) return;
  window.agAnalytics = true;
  const GA_MEASUREMENT_ID = 'G-FZR1VDVVK1';

  let ctx = {};
  try {
    ctx = JSON.parse(document.getElementById('AgAnalyticsContext').textContent);
  } catch (error) {}
  const currency = window.Shopify?.currency?.active || 'EUR';

  const queue = [];
  let customerType = null;
  const hasGtag = () => typeof window.gtag === 'function';

  const setCustomerType = (mode) => {
    const type = mode === 'excl' ? 'business' : mode === 'incl' ? 'consumer' : null;
    if (!type || type === customerType || !hasGtag()) return;
    customerType = type;
    window.gtag('set', 'user_properties', { customer_type: type });
  };

  const track = (name, params = {}) => {
    if (!hasGtag()) return void queue.push([name, params]);
    setCustomerType(window.VatSwitch?.mode);
    window.gtag('event', name, { ...params, send_to: GA_MEASUREMENT_ID });
  };

  let polls = 0;
  const poller = setInterval(() => {
    if (hasGtag()) {
      clearInterval(poller);
      queue.splice(0).forEach(([name, params]) => track(name, params));
    } else if (++polls > 120) {
      clearInterval(poller);
      queue.length = 0;
    }
  }, 250);

  // Product lists: the collection grid uses the collection's id and title, like the app's view_item_list.
  const PAGE_LABELS = { index: 'Home', product: 'Product', search: 'Zoeken', cart: 'Winkelwagen', collection: 'Collectie', blog: 'Blog', article: 'Blog' };
  const listOf = (section) => {
    const key = section.id.replace(/^shopify-section-(template--\d+__)?/, '');
    if (ctx.collection && key === 'product-grid') {
      return { item_list_id: ctx.collection.id, item_list_name: ctx.collection.title, fromApp: true };
    }
    if (ctx.pageType === 'search' && key === 'main') return { item_list_id: 'search', item_list_name: 'Zoekresultaten' };
    const label = ctx.pageType === 'page' ? `Pagina ${ctx.pageHandle}` : PAGE_LABELS[ctx.pageType] || ctx.pageType;
    const heading = section.querySelector('h2, .title, .h1')?.textContent.replace(/\s+/g, ' ').trim().slice(0, 60);
    return { item_list_id: `${ctx.template}:${key}`, item_list_name: `${label}: ${heading || key}` };
  };

  const itemOf = (card, index) => ({
    item_id: card.dataset.gaItemId,
    item_name: card.dataset.gaItemName,
    item_brand: card.dataset.gaItemBrand,
    price: Number(card.dataset.gaPrice),
    index,
  });

  const cardsIn = (section) => Array.from(section.querySelectorAll('.product-card-wrapper[data-ga-item-id]'));

  const sections = new Set(
    Array.from(document.querySelectorAll('.product-card-wrapper[data-ga-item-id]'), (card) => card.closest('.shopify-section')).filter(Boolean)
  );
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);
          const section = entry.target.closest('.shopify-section');
          const { fromApp, ...list } = listOf(section);
          if (fromApp) continue;
          const items = cardsIn(section).slice(0, 30).map(itemOf);
          track('view_item_list', { ...list, items });
        }
      },
      { threshold: 0.5 }
    );
    sections.forEach((section) => observer.observe(cardsIn(section)[0]));
  }

  // gtag holds hits back briefly and loses them when a link leaves the page (sendBeacon via transport_type does not
  // help), so a click that opens a product is stored and sent at the start of the next page, before that page's
  // view_item and add_to_cart. A click that keeps this page open (quick add, ctrl/cmd-click) is sent right away.
  const PENDING_KEY = 'agAnalyticsPending';
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || '[]');
    sessionStorage.removeItem(PENDING_KEY);
    pending.forEach(([name, params]) => track(name, params));
  } catch (error) {}

  const trackClick = (event, control, name, params) => {
    const leaves =
      control.tagName === 'A' && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && control.target !== '_blank';
    if (!leaves) return track(name, params);
    try {
      const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || '[]');
      sessionStorage.setItem(PENDING_KEY, JSON.stringify([...pending, [name, params]].slice(-5)));
    } catch (error) {
      track(name, params);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest('a[href], button');
      const card = control?.closest('.product-card-wrapper[data-ga-item-id]');
      if (card) {
        const section = card.closest('.shopify-section');
        const { fromApp, ...list } = listOf(section);
        return trackClick(event, control, 'select_item', {
          ...list,
          select_method: control.tagName === 'A' ? 'link' : 'quick_add',
          items: [itemOf(card, cardsIn(section).indexOf(card))],
        });
      }
      const suggestion = control?.closest('predictive-search a[href*="/products/"]');
      if (suggestion) {
        const name = suggestion.querySelector('.predictive-search__item-heading')?.textContent.trim() || suggestion.textContent.trim();
        trackClick(event, suggestion, 'select_item', {
          item_list_id: 'predictive_search',
          item_list_name: 'Zoeksuggesties',
          select_method: 'link',
          items: [{ item_name: name.slice(0, 100) }],
        });
      }
    },
    true
  );

  if (ctx.search) {
    track('search_results', {
      search_term: ctx.search.terms,
      results_count: ctx.search.count,
      search_outcome: ctx.search.count > 0 ? 'results' : 'no_results',
    });
  }

  if (ctx.product && !ctx.product.available) {
    const { item } = ctx.product;
    track('view_sold_out_item', {
      currency,
      value: item.price,
      product_title: item.item_name,
      product_brand: item.item_brand,
      items: [item],
    });
  }

  // Collection filters and sorting (Dawn's facet forms submit on every change).
  const facetLabel = (input) => {
    const label = input.closest('label') || document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    const text = label?.querySelector('[aria-hidden="true"]')?.textContent || label?.textContent || input.value;
    return text.replace(/\s+/g, ' ').replace(/\s*\(\d+\)\s*$/, '').trim().slice(0, 80);
  };
  document.addEventListener('change', (event) => {
    const input = event.target;
    const listName = ctx.collection?.title || (ctx.pageType === 'search' ? 'Zoekresultaten' : ctx.pageType);
    if (input.matches('select[name="sort_by"]')) {
      return track('sort_change', { sort_by: input.value, item_list_name: listName });
    }
    if (!input.closest('facet-filters-form') || !input.name?.startsWith('filter.')) return;
    if (input.type === 'checkbox' && !input.checked) return;
    const priceBound = input.name.match(/\.(gte|lte)$/);
    track('filter_apply', {
      filter_name: input.name.replace(/^filter\.[vp]\.(m\.)?/, '').replace(/\.(gte|lte)$/, ''),
      filter_value: priceBound ? `${priceBound[1] === 'gte' ? 'min' : 'max'} ${input.value}` : facetLabel(input),
      item_list_name: listName,
    });
  });

  // VAT Switch app: remember where the switch was clicked, then report the change.
  let switchPlacement = '';
  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest('.vatsw, .zc-quiz__switch');
      if (!control) return;
      const slot = control.closest('.vatsw-slot')?.className.match(/vatsw-slot--([\w-]+)/);
      switchPlacement = control.closest('.zc-quiz') ? 'quiz' : slot ? slot[1] : 'page';
    },
    true
  );
  document.addEventListener('vat-switch:change', (event) => {
    setCustomerType(event.detail?.mode);
    track('vat_switch', { vat_mode: event.detail?.mode, switch_placement: switchPlacement || 'other' });
    switchPlacement = '';
  });
})();
